import { useEffect, useMemo } from 'react';

import type { ApiListingApplicationId } from '#/api/types/ApiTypedId';
import { JobDetailsList } from '#/components/DetailsList/Job/JobDetailsList';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { ListingViewAdminTools } from '#/modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewAppliedMessage } from '#/modules/listing/components/ListingView/AppliedMessage/ListingViewAppliedMessage';
import { ListingViewApplyAts } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyAts';
import { ListingViewApplyExternal } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyExternal';
import { ListingViewIdealistAtsUserApplicationForm } from '#/modules/listing/components/ListingView/AtsApplicationForm/ListingViewIdealistAtsUserApplicationForm';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from '#/modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from '#/modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewBenefitsSection } from '#/modules/listing/components/ListingView/sections/ListingViewBenefitsSection';
import { ListingViewLanguageProficiencySection } from '#/modules/listing/components/ListingView/sections/ListingViewLanguageProficiencySection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { trackViewListingAtsSection } from '#/modules/listing/helpers/trackViewListingAtsSection';
import { useListingApplicationSubmission } from '#/modules/listing/hooks/listingApplicationSubmission/useListingApplicationSubmission';
import { useSaveListingAndOrg } from '#/modules/listing/hooks/useSaveListingAndOrg';
import type { ApiJob } from '#/modules/listing/jobs/api/types/ApiJob';
import { ViewJobActionButtons } from '#/modules/listing/jobs/components/ActionButtons/ViewJobActionButtons';
import { getJobApplySectionTitle } from '#/modules/listing/jobs/helpers/getJobApplySectionTitle';
import type { ListingTmpStorageListingApplicationFailure } from '#/modules/listing/types/ListingTmpStorageListingApplicationFailure';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { useSearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { isUserOrgOwner } from '#/utils/listings/getOrgOwner';
import { legacyIsExternalURL } from '#/utils/url/isExternalURL';
import { showModal } from '#/zustand-stores/modalStore';

import { ViewJobPageRecommendations } from './components/Recommendations/ViewJobPageRecommendations';
import { ViewJobPageSidebar } from './components/Sidebar/ViewJobPageSidebar';
import { UnpublishedJobToast } from './components/UnpublishedJobToast/UnpublishedJobToast';
import { useJobApplicationStatus } from './useJobApplicationStatus';

type Props = {
  job: ApiJob;
  initialListingApplicationId: ApiListingApplicationId | null;
  initiallySaved: boolean;
  orgInitiallySaved: boolean;
  listingApplicationFailure: ListingTmpStorageListingApplicationFailure | null;
};

export function ViewJobPage({
  job,
  initialListingApplicationId,
  initiallySaved,
  orgInitiallySaved,
  listingApplicationFailure,
}: Props) {
  const { i18n, locale } = useI18n();

  const {
    address,
    applyText,
    applyTextTruncated,
    applyUrl,
    applyEmail,
    benefits,
    benefitsTruncated,
    id,
    hasAts,
    startDate,
    endDate,
    functions,
    areasOfFocus,
    requestedAttachments,
    requestedAttachmentsHash,
    isPostedAnonymously,
    name,
    type,
    description,
    descriptionTruncated,
    remoteOptions,
    url,
    org,
    orgNameOverride,
    applicationDeadline,
    education,
    isContract,
    isFullTime,
    isTemporary,
    professionalLevel,
    salaryCurrency,
    salaryDetails,
    salaryMaximum,
    salaryMinimum,
    salaryPeriod,
    synonym,
    languageProficiency,
    languageProficiencyTruncated,
    firstPublished,
  } = job;

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, org?.id);

  const { searchContext } = useSearchContextHeader({ currentObjectId: id });
  const { searchSavedListingsStore } = useSearchSavedListingsStore();

  const {
    save,
    saved,
    unsave,
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveListingAndOrg({
    listing: job,
    initiallySaved,
    orgInitiallySaved,
    searchSavedListingsStore,
  });

  const { listingApplicationId, markAsApplied, markAsNotApplied, onApplied } =
    useJobApplicationStatus({ job, initialListingApplicationId });
  const applied = Boolean(listingApplicationId);

  const listingDetailsSearchResultsEnabled = useMemo(() => {
    if (isOrgOwner && !searchContext) return false;

    return true;
  }, [isOrgOwner, searchContext]);

  useEffect(() => {
    trackViewListing({
      status: 'published',
      hasAts,
      listingName: name,
      listingId: id,
      orgId: org?.id,
      orgName: org?.name,
      orgType: org?.orgType,
      listingType: 'JOB',
    });
  }, [hasAts, id, name, org?.id, org?.name, org?.orgType]);

  const orgId = org?.id ?? null;
  const orgName = orgNameOverride ?? org?.name ?? null;
  const orgUrl = org?.url[locale] ?? null;
  const orgType = org?.orgType ?? null;

  const {
    listingApplicationInitialValues,
    listingApplicationInitialErrors,
    submitListingApplication,
  } = useListingApplicationSubmission({
    listingId: id,
    listingType: type,
    orgId,
    orgType,
    applied,
    onApplied,
    listingApplicationFailure,
  });

  return (
    <>
      {job && <UnpublishedJobToast job={job} />}
      {isOrgOwner && org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this job')}
          tools={
            <ListingViewAdminTools
              listingId={id}
              listingType="JOB"
              orgId={org.id}
              hasAts={hasAts}
              canPost
            />
          }
        />
      )}

      <ListingViewBody>
        <ViewJobPageSidebar
          job={job}
          applied={applied}
          listingDetailsSearchResultsEnabled={
            listingDetailsSearchResultsEnabled
          }
          searchContext={searchContext}
          searchSavedListingsStore={searchSavedListingsStore}
          listingViewStatus={ListingViewStatus.PUBLISHED}
        />

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={address}
            isPostedAnonymously={isPostedAnonymously}
            listingId={id}
            listingName={name}
            listingType={type}
            listingFirstPublished={firstPublished}
            orgName={orgName}
            orgType={orgType}
            orgUrl={orgUrl}
            remoteOptions={remoteOptions}
            listingViewStatus={ListingViewStatus.PUBLISHED}
            actionButtons={
              <ViewJobActionButtons
                job={job}
                applied={applied}
                onMarkAsApplied={markAsApplied}
                onMarkAsNotApplied={markAsNotApplied}
                jobSaved={saved}
                onSaveJob={save}
                onUnsaveJob={unsave}
                orgSaved={orgSaved}
                onSaveOrg={saveOrg}
                onUnsaveOrg={unsaveOrg}
                shouldShowSaveOrgTooltip={shouldShowSaveOrgTooltip}
                onDismissSaveOrgTooltip={dismissSaveOrgTooltip}
              />
            }
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <JobDetailsList
              orgAreasOfFocus={org?.areasOfFocusKeys}
              startDate={startDate}
              endDate={endDate}
              applicationDeadline={applicationDeadline}
              education={education}
              isContract={isContract}
              isFullTime={isFullTime}
              isTemporary={isTemporary}
              professionalLevel={professionalLevel}
              salaryCurrency={salaryCurrency}
              salaryDetails={salaryDetails}
              salaryMaximum={salaryMaximum}
              salaryMinimum={salaryMinimum}
              salaryPeriod={salaryPeriod}
              jobCountry={address.country}
              areasOfFocus={areasOfFocus}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={name}
            image={null}
            description={description}
            descriptionTruncated={descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && legacyIsExternalURL(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: functions,
                  orgId: org?.id,
                  listingName: name,
                  listingType: 'JOB',
                  synonym,
                });
              }
            }}
            showMoreLinkTitle={i18n('Read more about this job')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: job.type,
                locale: job.locale,
              });
            }}
          />

          <ListingViewBenefitsSection
            benefits={benefits}
            benefitsTruncated={benefitsTruncated}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about benefits', {
                listing_type: job.type,
                locale: job.locale,
              });
            }}
          />

          <ListingViewLanguageProficiencySection
            languageProficiency={languageProficiency}
            languageProficiencyTruncated={languageProficiencyTruncated}
          />

          <ListingViewLocationSection
            listingType="JOB"
            address={address}
            remoteOptions={remoteOptions}
          />

          <ListingViewApplySection
            title={getJobApplySectionTitle({ i18n, hasAts })}
          >
            {hasAts ? (
              // oxlint-disable-next-line react/jsx-no-useless-fragment
              <>
                {applied ? (
                  <ListingViewAppliedMessage />
                ) : (
                  <ListingViewApplyAts
                    applyText={applyText}
                    applicationForm={
                      <ListingViewIdealistAtsUserApplicationForm
                        listingId={id}
                        listingType="JOB"
                        requestedAttachments={requestedAttachments}
                        submission={{
                          listingApplicationInitialValues,
                          listingApplicationInitialErrors,
                          onSubmit: submitListingApplication,
                          requestedAttachmentsHash,
                        }}
                      />
                    }
                    onReach={() =>
                      trackViewListingAtsSection({
                        listingId: id,
                        listingType: 'JOB',
                        orgId,
                        orgType,
                        applied,
                      })
                    }
                  />
                )}
              </>
            ) : (
              <ListingViewApplyExternal
                applyEmail={applyEmail}
                applyText={applyText}
                applyTextTruncated={applyTextTruncated}
                applyUrl={applyUrl}
                functions={functions}
                listingName={name}
                listingType="JOB"
                listingId={id}
                orgId={orgId}
                orgType={orgType}
                synonym={synonym}
                applied={applied}
                onMarkAsApplied={markAsApplied}
                isPreview={false}
              />
            )}
          </ListingViewApplySection>

          {searchContext ? (
            <PrintHidden>
              <div style={{ marginTop: 70 }}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </div>
            </PrintHidden>
          ) : null}
        </ListingViewMain>
      </ListingViewBody>

      <ListingViewFooter
        hiddenOnDesktop={Boolean(user && listingDetailsSearchResultsEnabled)}
      >
        <ViewJobPageRecommendations
          jobId={id}
          jobTitle={name}
          synonym={synonym}
          functions={job.functions}
          orgAreasOfFocus={org?.areasOfFocusKeys}
        />
        {!user && <ListingViewSignupCta listingUrl={url[locale]} type={type} />}
      </ListingViewFooter>
    </>
  );
}
